import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import { getDeepestCommonCategoryId } from '../../helpers/getDeepestCommonCategoryId.js'
import {
  setCategoriaPath,
  setEstado,
  setOrden,
  setTipoVendedor
} from '../../slices/filterSlice.js'
import { CarritoContext } from '../context/carritoContext'
import { Filtros } from '../reutilizables/filtros/Filtros'
import GenericSelect from '../reutilizables/genericSelect/GenericSelect.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import {
  BuscarOfertas,
  buscarPorSubCategoria,
  buscarTodasOptimizado
} from '../servicios/publicacionesService'
import { buscarTodas } from '../servicios/tiendasOficialesService'
import Banners from './Banners'
import ComponenteError from './ComponenteError'
import Publicacion from './Publicacion'
import styles from './Publicaciones.module.css'

const ITEMS_PAGINA = 15
const FILTRO_MENOR_PRECIO = 2
const FILTRO_MAYOR_PRECIO = 3

const Publicaciones = ({ ofertas }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const topRef = useRef(null)

  const { getCarrito } = useContext(CarritoContext)

  const { idVendedorCarrito, razonSocial } = location.state || {}
  const idComprador = parseInt(localStorage.getItem('idUsuario'))
  const { idSubCategoria, idVendedor } = useParams()

  // Access the path from router state
  const tiendaPath = location.state?.path

  const [vistaCuadricula, setVistaCuadricula] = useState(true)
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(ITEMS_PAGINA)
  const [vendedor, setVendedor] = useState({
    id: idVendedorCarrito || 0,
    label: razonSocial || ''
  })
  const [state, setState] = useState({
    publicaciones: [],
    tiendas: [],
    ofertas: [],
    buscar: '',
    idVendedor: 0,
    idCategoria: 0,
    sinResultados: false
  })
  const [publicacionesMostradas, setPublicacionesMostradas] = useState([])
  const { busca, orden, publicacionesFiltradas, tipoVendedor, estado } = useSelector(
    (state) => state.filters
  )

  const showItems = useCallback(async () => {
    let publicaciones = []

    if (ofertas === 'S') {
      const res = await BuscarOfertas(idComprador, state.idVendedor, state.idCategoria)
      publicaciones = res.data
    } else if (idVendedor || vendedor.id) {
      const res = await buscarTodasOptimizado(
        idComprador,
        idVendedor || vendedor.id,
        0,
        busca || ''
      )
      publicaciones = res.data

      // Tiendas - moved inside this block to avoid dependency issues
      const { data: tiendas } = await buscarTodas(busca || '')
      const tienda = tiendas.filter((t) => t.idUsuario === Number(idVendedor))
      setState((prev) => ({ ...prev, tiendas: tienda }))
    } else if (idSubCategoria && idSubCategoria !== '') {
      const res = await buscarPorSubCategoria(idSubCategoria, busca || '')
      publicaciones = res.data
      const categoria = [...new Set(publicaciones.map((p) => p.idCategoria))]
      setState((prev) => ({ ...prev, idSubCategoria: categoria }))
    } else {
      const res = await buscarTodasOptimizado(idComprador, 0, 0, busca || '')
      publicaciones = res.data
    }
    const deepestCategory = getDeepestCommonCategoryId(publicaciones)
    if (deepestCategory) {
      dispatch(setCategoriaPath(deepestCategory))
    }

    setState((prev) => ({
      ...prev,
      publicaciones,
      sinResultados: publicaciones.length === 0,
      buscar: busca
    }))
  }, [ofertas, vendedor, idSubCategoria, busca, idComprador, location.pathname])

  const aplicarOrdenamiento = (publicaciones) => {
    const ventasPorVendedor = {}
    publicaciones.forEach((pub) => {
      if (!ventasPorVendedor[pub.idVendedor]) {
        ventasPorVendedor[pub.idVendedor] = 0
      }
      ventasPorVendedor[pub.idVendedor] += pub.totalItemsPedidos || 0
    })

    return [...publicaciones].sort((a, b) => {
      if (orden === FILTRO_MENOR_PRECIO) {
        return a.precio - b.precio
      }
      if (orden === FILTRO_MAYOR_PRECIO) {
        return b.precio - a.precio
      }

      if (a.idPublicacionExposicion !== b.idPublicacionExposicion) {
        return a.idPublicacionExposicion - b.idPublicacionExposicion
      }
      const ventasProductoA = a.totalItemsPedidos || 0
      const ventasProductoB = b.totalItemsPedidos || 0
      if (ventasProductoA !== ventasProductoB) {
        return ventasProductoB - ventasProductoA
      }
      const ventasVendedorA = ventasPorVendedor[a.idVendedor] || 0
      const ventasVendedorB = ventasPorVendedor[b.idVendedor] || 0
      return ventasVendedorB - ventasVendedorA
    })
  }

  const onToggleClick = (event, publicacion, idPublicacion) => {
    navigate(`/Publicacion/${idPublicacion}`)
  }

  // Cargar publicaciones al montar o cambiar filtros
  useEffect(() => {
    showItems()
  }, [showItems])

  useEffect(() => {
    getCarrito()
  }, [])

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      sinResultados: publicacionesFiltradas.length === 0
    }))
  }, [publicacionesFiltradas])

  useEffect(() => {
    if (publicacionesFiltradas && publicacionesFiltradas.length > 0) {
      const publicacionesOrdenadas = aplicarOrdenamiento(publicacionesFiltradas)
      const indiceUltimoItem = paginaActual * itemsPorPagina
      const indicePrimerItem = indiceUltimoItem - itemsPorPagina
      setPublicacionesMostradas(
        publicacionesOrdenadas.slice(indicePrimerItem, indiceUltimoItem)
      )
    } else {
      setPublicacionesMostradas([])
    }
  }, [publicacionesFiltradas, paginaActual, itemsPorPagina, orden])

  const banner = state.tiendas.filter((t) => t.esBanner === true)
  const arrBannersId = [...new Set(banner.map((t) => t.idTiendaOficial && t.path))]
  const nombreTienda = state.tiendas.map((t) => t.nombreTienda)
  const nombre = [...new Set(nombreTienda.map((n) => n))].toString()

  const filtrosFijos = [
    {
      titulo: 'Vendedor',
      value: tipoVendedor,
      opciones: [
        { label: 'Empresa', valor: 4 },
        { label: 'Particular', valor: 3 }
      ],
      onChange: (v) => dispatch(setTipoVendedor(v))
    },
    {
      titulo: 'Estado',
      value: estado,
      opciones: [
        { label: 'Nuevo', valor: 'nuevo' },
        { label: 'Usado', valor: 'usado' }
      ],
      onChange: (v) => dispatch(setEstado(v))
    }
  ]

  return (
    <div className={styles.layout} ref={topRef}>
      <aside className={styles.filtros}>
        <Filtros
          publicaciones={state.publicaciones}
          filtrosFijos={filtrosFijos}
          setVendedor={setVendedor}
          vendedor={vendedor}
        />
      </aside>
      <div className="col">
        {idVendedor && (
          <Banners src={arrBannersId} logoPath={tiendaPath} nombreTienda={nombre} />
        )}
        <fieldset className={`fieldset ${styles.fieldset}`}>
          <div className={styles.headerPublicaciones}>
            <legend>Publicaciones</legend>
            <div className={styles.accionesVista}>
              <GenericSelect
                options={[
                  { label: 'Más recientes', value: 1 },
                  { label: 'Menor precio', value: 2 },
                  { label: 'Mayor precio', value: 3 }
                ]}
                value={orden}
                onChange={(e) => dispatch(setOrden(e))}
                label="Ordenar por"
              />
              <button
                className={`${styles.buttonVista} ${
                  vistaCuadricula ? styles.activo : ''
                }`}
                onClick={() => setVistaCuadricula(true)}
                title="Vista en cuadrícula"
              >
                <i className="fa fa-th" />
              </button>
              <button
                className={`${styles.buttonVista} ${
                  vistaCuadricula ? '' : styles.activo
                }`}
                onClick={() => setVistaCuadricula(false)}
                title="Vista en lista"
              >
                <i className="fa fa-bars" />
              </button>
            </div>
          </div>
          {!state.sinResultados ? (
            <>
              <div className={vistaCuadricula ? styles.gridContainer : ''}>
                {publicacionesMostradas.map((publicacion) => (
                  <div
                    key={publicacion.idPublicacion}
                    className={vistaCuadricula ? '' : styles.publicacion}
                  >
                    <Publicacion
                      onToggleClick={onToggleClick}
                      publicacion={publicacion}
                      vistaCuadricula={vistaCuadricula}
                    />
                  </div>
                ))}
              </div>
              <Paginacion
                totalPaginas={Math.ceil(publicacionesFiltradas.length / itemsPorPagina)}
                paginaActual={paginaActual}
                onCambioPagina={(num) => {
                  setPaginaActual(num)
                  topRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
                itemsPorPagina={itemsPorPagina}
                onCambioItemsPorPagina={(num) => {
                  setItemsPorPagina(num)
                  setPaginaActual(1)
                  topRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
              />
            </>
          ) : (
            <div className="row">
              <ComponenteError tipo="I" idAviso={7} />
            </div>
          )}
        </fieldset>
      </div>
    </div>
  )
}

Publicaciones.propTypes = {
  ofertas: PropTypes.string
}

export default Publicaciones
